import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';

export default function RefundPolicyPage() {
  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen pb-20 transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-300' : 'bg-white text-gray-700'}`}>
      <SEO
        {...pageSeoMeta.policyRefund}
        jsonLd={buildBreadcrumbJsonLd([
          { name: 'Home', url: '/' },
          { name: 'Refund Policy', url: '/policies/refund' },
        ])}
      />

      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Refund Policy' },
      ]} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <Link to="/" className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 mb-8 text-sm font-medium">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className={`text-3xl sm:text-4xl font-extrabold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Refund Policy</h1>
          <p className={`text-sm mb-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Last updated: March 2026</p>

          <div className="space-y-8 leading-relaxed">
            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Overview</h2>
              <p>At ZeroSmoke, we want you to be completely satisfied with your purchase. If you're not happy with your order, we're here to help.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Eligibility for Refunds</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Items must be returned within <strong>7 days</strong> of delivery.</li>
                <li>Products must be <strong>unopened and in original packaging</strong>.</li>
                <li>Proof of purchase (order number or receipt) is required.</li>
                <li>Opened or used products cannot be returned due to hygiene and safety regulations.</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Refund Process</h2>
              <ol className="list-decimal list-inside space-y-2">
                <li>Contact us at <a href="mailto:zerosmoke2026@gmail.com" className="text-emerald-500 hover:underline">zerosmoke2026@gmail.com</a> with your order details.</li>
                <li>We will review your request within 2 business days.</li>
                <li>Once approved, ship the product back to our designated address.</li>
                <li>Refund will be processed within <strong>5-7 business days</strong> after we receive and inspect the returned item.</li>
              </ol>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Damaged or Incorrect Items</h2>
              <p>If you receive a damaged or incorrect item, please contact us within <strong>48 hours</strong> of delivery with photos. We will arrange a replacement or full refund at no extra cost.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Non-Refundable Items</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Opened or partially used pouches/cans.</li>
                <li>Items purchased during clearance sales (marked as final sale).</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>How to Request a Refund</h2>
              <p>To initiate a refund or raise any queries, simply send us an email at <a href="mailto:zerosmoke2026@gmail.com" className="text-emerald-500 hover:underline">zerosmoke2026@gmail.com</a> with your order number and a brief description of the issue. Our team will review your request and get back to you <strong>within 48 hours</strong>.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Contact Us</h2>
              <p>For any refund-related questions or concerns, reach out to us at <a href="mailto:zerosmoke2026@gmail.com" className="text-emerald-500 hover:underline">zerosmoke2026@gmail.com</a>. You can also DM us on <a href="https://www.instagram.com/zerosmoke.in_2026/" target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:underline">Instagram</a>. We're happy to help!</p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
